import { useRef, useState, useCallback, useEffect } from 'react';
import musicSrc from '../assets/sounds/castledatadream.mp3';

const CACHE_NAME = 'survivor-audio-v1';

/**
 * Loads the audio file through the Cache API.
 *
 * First visit  → fetches from /static/media/, stores response in the cache,
 *                returns a local blob: URL (no future network hit needed).
 * Later visits → reads directly from the cache and returns a blob: URL.
 * Fallback     → if Cache API is unavailable (non-secure context, etc.) the
 *                original webpack URL is returned so playback still works.
 *
 * Returns { url: string, isBlob: boolean }
 * Caller must call URL.revokeObjectURL(url) when done if isBlob === true.
 */
async function getCachedAudioUrl(src) {
  if (typeof caches === 'undefined') return { url: src, isBlob: false };
  try {
    const cache = await caches.open(CACHE_NAME);
    let response = await cache.match(src);
    if (!response) {
      // First load — fetch and persist in the cache
      await cache.add(src);
      response = await cache.match(src);
    }
    if (response) {
      const blob = await response.blob();
      return { url: URL.createObjectURL(blob), isBlob: true };
    }
  } catch {
    // Cache API threw (quota exceeded, private browsing restriction, etc.)
  }
  return { url: src, isBlob: false };
}

/**
 * Manages background music playback.
 * Tries to autoplay on mount; falls back to first user interaction
 * (required by browser autoplay policy).
 *
 * The audio file is loaded via the Cache API so it is only fetched from the
 * network once — subsequent page loads serve it from the local cache as a
 * blob: URL, triggering no /static/media/ request.
 *
 * Returns: { volume, muted, setVolume, toggleMute }
 */
export function useBackgroundMusic() {
  const audioRef   = useRef(null);
  const [volume, setVolumeState] = useState(0.5);
  const [muted,  setMutedState]  = useState(false);

  useEffect(() => {
    let cancelled = false;
    let blobUrl   = null;

    // Define onInteract outside the async callback so the cleanup can always
    // remove it, regardless of whether the async call has resolved yet.
    const onInteract = () => {
      if (audioRef.current) audioRef.current.play().catch(() => {});
      document.removeEventListener('click',   onInteract);
      document.removeEventListener('keydown', onInteract);
    };

    getCachedAudioUrl(musicSrc).then(({ url, isBlob }) => {
      if (cancelled) {
        // Component unmounted before we resolved — clean up the blob immediately
        if (isBlob) URL.revokeObjectURL(url);
        return;
      }

      if (isBlob) blobUrl = url;

      const audio  = new Audio(url);
      audio.loop   = true;
      audio.volume = 0.5;
      audioRef.current = audio;

      // Attempt immediate play (blocked by browsers until user gesture)
      audio.play().catch(() => {});

      // Guaranteed start on first click or keypress
      document.addEventListener('click',   onInteract);
      document.addEventListener('keydown', onInteract);
    });

    return () => {
      cancelled = true;

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      // Revoke blob URL to free the in-memory reference
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        blobUrl = null;
      }

      document.removeEventListener('click',   onInteract);
      document.removeEventListener('keydown', onInteract);
    };
  }, []);

  const setVolume = useCallback((v) => {
    const clamped = Math.max(0, Math.min(1, v));
    setVolumeState(clamped);
    if (audioRef.current) audioRef.current.volume = clamped;
  }, []);

  const toggleMute = useCallback(() => {
    setMutedState(prev => {
      const next = !prev;
      if (audioRef.current) audioRef.current.muted = next;
      return next;
    });
  }, []);

  return { volume, muted, setVolume, toggleMute };
}
